/**
 * Formulario de "Novo Veiculo" — portado do prototipo (`VeiculoForm.tsx`), com os
 * campos que a tabela `veiculos` de fato tem (db/migrations/0002).
 *
 * O QUE FICOU DE FORA, DE PROPOSITO:
 * - `DocumentChecklist`: pertence a um modulo de documentos/OCR que nao existe
 *   neste repositorio.
 * - Selects dinamicos de marca e cor (`useParametroOptions`): alimentados por uma
 *   API de "parametros" que nao existe aqui. As colunas `marca` e `cor` do banco
 *   sao texto livre, nao chave estrangeira — viram `Input` simples.
 * - Checagem de placa duplicada ao vivo contra o banco: o prototipo consultava o banco
 *   dele direto do navegador. Nao ha rota propria na API pra isso ainda: o formulario
 *   compara contra o mock que a listagem ja usa (`placaJaCadastrada`, `dados.ts`), o
 *   melhor que da sem uma rota real.
 * - `RegistrarVersaoModal` / historico de alteracao sensivel: so faz sentido em
 *   edicao, e este formulario e so de criacao.
 *
 * O "Salvar" NAO CHAMA API NENHUMA — nenhuma tabela cadastral aceita escrita
 * hoje (docs/ALVOS.md, Onda 2; ver `comum/cadastroIndisponivel.ts`). Ele so
 * mostra o aviso e mantem os dados no formulario.
 */

import { zodResolver } from '@hookform/resolvers/zod';

import { ComoFuncionaAIntegracao } from '@/modulos/admin/catalogo-veiculos/ComoFuncionaAIntegracao';
import {
  Accessibility,
  Bus,
  DollarSign,
  Info,
  MessageSquare,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CredenciadoCombobox } from '@/modulos/operacao/comum/CredenciadoCombobox';
import { TitularCombobox } from '@/modulos/operacao/comum/TitularCombobox';
import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { OPCOES_DE_SITUACAO_DA_PESSOA } from '@/modulos/operacao/comum/contrato';
import { placaJaCadastrada } from '@/modulos/operacao/veiculos/dados';
import { mascaraPlaca, somenteDigitos } from '@/nucleo/formato/documentos';
import {
  carregarRascunho,
  limparRascunho,
  useRascunhoDeFormulario,
} from '@/nucleo/ganchos/useRascunhoDeFormulario';
import { MENSAGEM_DE_PLACA_INVALIDA, normalizarPlaca, validarPlaca } from '@/nucleo/validarPlaca';
import { AvisoDeRascunho } from '@/ui/AvisoDeRascunho';
import { MoneyInput } from '@/ui/MoneyInput';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import { FormSection } from '@/ui/FormSection';
import { RodapeDoFormularioEmModal } from '@/ui/forms/RodapeDoFormularioEmModal';

const CHAVE_DO_RASCUNHO = 'veiculo-form';

const TIPOS_DE_COMBUSTIVEL = [
  'GASOLINA',
  'ETANOL',
  'FLEX',
  'DIESEL',
  'GNV',
  'HIBRIDO',
  'ELETRICO',
  'OUTRO',
] as const;

const OPCOES_DE_TIPO_DE_COMBUSTIVEL: readonly {
  value: (typeof TIPOS_DE_COMBUSTIVEL)[number];
  label: string;
}[] = [
  { value: 'GASOLINA', label: 'Gasolina' },
  { value: 'ETANOL', label: 'Etanol' },
  { value: 'FLEX', label: 'Flex' },
  { value: 'DIESEL', label: 'Diesel' },
  { value: 'GNV', label: 'GNV' },
  { value: 'HIBRIDO', label: 'Híbrido' },
  { value: 'ELETRICO', label: 'Elétrico' },
  { value: 'OUTRO', label: 'Outro' },
];

const TIPOS_DE_BANCADA = ['CONVENCIONAL', 'CRECHE', 'ACESSIVEL'] as const;

const OPCOES_DE_TIPO_DE_BANCADA: readonly {
  value: (typeof TIPOS_DE_BANCADA)[number];
  label: string;
}[] = [
  { value: 'CONVENCIONAL', label: 'Convencional' },
  { value: 'CRECHE', label: 'Creche' },
  { value: 'ACESSIVEL', label: 'Acessível' },
];

const ORIGENS_DE_CAPACIDADE_TOTAL = ['SGTP', 'CRLV', 'MANUAL'] as const;

const OPCOES_DE_ORIGEM_DE_CAPACIDADE_TOTAL: readonly {
  value: (typeof ORIGENS_DE_CAPACIDADE_TOTAL)[number];
  label: string;
}[] = [
  { value: 'SGTP', label: 'SGTP' },
  { value: 'CRLV', label: 'CRLV' },
  { value: 'MANUAL', label: 'Manual' },
];

const esquema = z
  .object({
    placa: z.string().min(1, 'Placa obrigatória'),
    status: z.enum(['ATIVO', 'INATIVO', 'SUSPENSO']),
    marca: z.string().trim().min(1, 'Marca é obrigatória').max(50),
    modelo: z.string().trim().min(1, 'Modelo é obrigatório').max(50),
    cor: z.string().max(30).optional(),
    ano_fabricacao: z.number().int().min(1990).max(2030).nullable(),
    ano_modelo: z.number().int().min(1990).max(2030).nullable(),
    chassi: z.string().max(17).optional(),
    renavam: z.string().max(11).optional(),
    tipo_combustivel: z.enum(TIPOS_DE_COMBUSTIVEL).optional().or(z.literal('')),
    crm: z.string().max(10).optional(),
    credenciado_id: z.string().nullable(),
    titular_atual_id: z.string().nullable(),
    capacidade_detran: z.number().int().min(0).max(100),
    capacidade_teg: z.number().int().min(0).max(100),
    capacidade_creche: z.number().int().min(0).max(100),
    capacidade_acessivel: z.number().int().min(0).max(20),
    capacidade_total: z.number().int().min(0).max(100).nullable(),
    origem_capacidade_total: z.enum(ORIGENS_DE_CAPACIDADE_TOTAL).optional().or(z.literal('')),
    acessivel: z.boolean(),
    valor_mensal: z.string().optional(),
    seguro_apolice: z.string().max(100).optional(),
    seguro_seguradora: z.string().max(100).optional(),
    seguro_validade: z.string().optional(),
    crlv_validade: z.string().optional(),
    crm_pmsp_validade: z.string().optional(),
    vistoria_validade: z.string().optional(),
    tacografo_validade: z.string().optional(),
    seguro_app_valor: z.string().optional(),
    seguro_app_vencimento: z.string().optional(),
    seguro_rc_valor: z.string().optional(),
    seguro_rc_vencimento: z.string().optional(),
    tipo_bancada: z.enum(TIPOS_DE_BANCADA).optional().or(z.literal('')),
    observacoes: z.string().max(1000).optional(),
  })
  .superRefine((valores, ctx) => {
    const limpa = normalizarPlaca(valores.placa);
    if (limpa.length < 7) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['placa'],
        message: 'Placa deve ter 7 caracteres',
      });
    } else if (!validarPlaca(valores.placa)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['placa'],
        message: MENSAGEM_DE_PLACA_INVALIDA,
      });
    } else if (placaJaCadastrada(limpa)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['placa'],
        message: 'Já existe um veículo cadastrado com esta placa.',
      });
    }
  });

export type ValoresDoFormulario = z.infer<typeof esquema>;

const VALORES_INICIAIS: ValoresDoFormulario = {
  placa: '',
  status: 'ATIVO',
  marca: '',
  modelo: '',
  cor: '',
  ano_fabricacao: null,
  ano_modelo: null,
  chassi: '',
  renavam: '',
  tipo_combustivel: '',
  crm: '',
  credenciado_id: null,
  titular_atual_id: null,
  capacidade_detran: 0,
  capacidade_teg: 0,
  capacidade_creche: 0,
  capacidade_acessivel: 0,
  capacidade_total: null,
  origem_capacidade_total: '',
  acessivel: false,
  valor_mensal: '',
  seguro_apolice: '',
  seguro_seguradora: '',
  seguro_validade: '',
  crlv_validade: '',
  crm_pmsp_validade: '',
  vistoria_validade: '',
  tacografo_validade: '',
  seguro_app_valor: '',
  seguro_app_vencimento: '',
  seguro_rc_valor: '',
  seguro_rc_vencimento: '',
  tipo_bancada: '',
  observacoes: '',
};

export interface VeiculoFormularioProps {
  onCancelar: () => void;
  /**
   * Pre-preenche o formulario com os dados de um veiculo existente — usado pela tela de
   * edicao (`VeiculoEditarPage`). Sem isto, o formulario abre em branco (caso de "Novo
   * Veiculo").
   */
  valoresIniciais?: Partial<ValoresDoFormulario>;
  /**
   * `true` quando este formulario roda numa PAGINA inteira (`VeiculoEditarPage`),
   * sem `ResponsiveModal` por perto para prover o rodape fixo — sem isto o rodape
   * (Cancelar/Salvar) nao apareceria (`RodapeDoFormularioEmModal` so renderiza algo
   * quando `inline` OU quando ha contexto de modal). Default `false`: mantem o
   * comportamento de "Novo Veiculo" (dentro de `ResponsiveModal`) inalterado.
   */
  rodapeInline?: boolean;
}

export function VeiculoFormulario({
  onCancelar,
  valoresIniciais,
  rodapeInline,
}: VeiculoFormularioProps) {
  const idBase = useId();
  const formId = `veiculo-formulario-${idBase.replace(/:/g, '')}`;

  const [mostrarAvisoDeRascunho, setMostrarAvisoDeRascunho] = useState(
    () => carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO) !== null,
  );

  const form = useForm<ValoresDoFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: { ...VALORES_INICIAIS, ...valoresIniciais },
    mode: 'onSubmit',
  });

  const valores = form.watch();
  const acessivel = form.watch('acessivel');
  const capacidadeTotal = form.watch('capacidade_total');
  const gravadoEm = useRascunhoDeFormulario(CHAVE_DO_RASCUNHO, valores, true);

  function restaurarRascunho() {
    const rascunho = carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO);
    if (rascunho) {
      form.reset(rascunho);
    }
    setMostrarAvisoDeRascunho(false);
  }

  function descartarRascunho() {
    limparRascunho(CHAVE_DO_RASCUNHO);
    setMostrarAvisoDeRascunho(false);
  }

  function enviar() {
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(enviar)} className="space-y-6" noValidate>
        {mostrarAvisoDeRascunho && (
          <AvisoDeRascunho
            gravadoEm={gravadoEm}
            aoRestaurar={restaurarRascunho}
            aoDescartar={descartarRascunho}
          />
        )}

        <FormSection
          title="Identificação do Veículo"
          subtitle="Dados obrigatórios para cadastro"
          icon={Bus}
        >
          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="placa"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Placa</FormLabel>
                  <FormControl>
                    <Input
                      value={mascaraPlaca(field.value)}
                      onChange={(e) => field.onChange(normalizarPlaca(e.target.value))}
                      maxLength={8}
                      placeholder="ABC-1D23"
                      className="font-mono uppercase"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_SITUACAO_DA_PESSOA.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="marca"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Marca</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={50} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <p className="text-muted-foreground">
              O catálogo de marcas, modelos e versões do TEG é conferido todo dia com a fonte
              oficial.
            </p>
            <ComoFuncionaAIntegracao className="shrink-0" />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="modelo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={50}
                      placeholder="Ex: Sprinter 415"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cor</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={30} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tipo_combustivel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de combustível</FormLabel>
                  <Select value={field.value || ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Não informado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_TIPO_DE_COMBUSTIVEL.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-4">
            <FormField
              control={form.control}
              name="ano_fabricacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ano Fabricação</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? null : Number(e.target.value))
                      }
                      min={1990}
                      max={2030}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ano_modelo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ano Modelo</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? null : Number(e.target.value))
                      }
                      min={1990}
                      max={2030}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="chassi"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chassi</FormLabel>
                  <FormControl>
                    <Input
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      maxLength={17}
                      className="font-mono uppercase"
                      placeholder="17 caracteres"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="renavam"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RENAVAM</FormLabel>
                  <FormControl>
                    <Input
                      value={field.value}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      maxLength={11}
                      className="font-mono"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="crm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CRM</FormLabel>
                  <FormControl>
                    <Input
                      value={field.value}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      maxLength={10}
                      className="font-mono"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="credenciado_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credenciado</FormLabel>
                  <CredenciadoCombobox
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel="Credenciado"
                  />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          title="Titular do Veículo"
          subtitle="Pessoa física ou jurídica proprietária do veículo"
          icon={User}
        >
          <FormField
            control={form.control}
            name="titular_atual_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Titular</FormLabel>
                <TitularCombobox
                  value={field.value}
                  onChange={field.onChange}
                  ariaLabel="Titular do veículo"
                />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          title="Capacidades e Recursos"
          subtitle="Lotação autorizada e características do veículo"
          icon={Users}
        >
          <div className="grid gap-5 sm:grid-cols-4">
            <FormField
              control={form.control}
              name="capacidade_detran"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cap. DETRAN</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
                      }
                      min={0}
                      max={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacidade_teg"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-1.5">
                    <FormLabel>Cap. TEG</FormLabel>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="Sobre a capacidade TEG"
                            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex h-4 w-4 items-center justify-center rounded-full focus:outline-hidden focus-visible:ring-2"
                          >
                            <Info className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          O banco calcula este valor automaticamente como capacidade DETRAN menos 1
                          ao salvar. Normalmente não precisa ser digitado à mão.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
                      }
                      min={0}
                      max={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacidade_creche"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cap. Creche</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
                      }
                      min={0}
                      max={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacidade_acessivel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nº ancoragens p/ cadeira de rodas</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
                      }
                      min={0}
                      max={20}
                      disabled={!acessivel}
                      placeholder={acessivel ? '0' : '—'}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="capacidade_total"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cap. Total do veículo</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? null : Number(e.target.value))
                      }
                      min={0}
                      max={100}
                      placeholder="—"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="origem_capacidade_total"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Origem da Cap. Total</FormLabel>
                  <Select
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    disabled={capacidadeTotal === null}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_ORIGEM_DE_CAPACIDADE_TOTAL.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="acessivel"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3 space-y-0">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="mt-0! flex cursor-pointer items-center gap-1.5">
                  <Accessibility className="text-muted-foreground h-4 w-4" aria-hidden="true" />
                  Veículo Acessível (PCD)
                </FormLabel>
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection title="Valor" subtitle="Remuneração mensal estimada" icon={DollarSign}>
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="valor_mensal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor Mensal</FormLabel>
                  <FormControl>
                    <MoneyInput value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          title="Documentos do veículo"
          subtitle="Validades regulatórias e apólices de seguro"
          icon={ShieldCheck}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="crlv_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CRLV Validade (Detran)</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="crm_pmsp_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Validade do CRM (Prefeitura de SP / SMT)</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="vistoria_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vistoria Validade</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tacografo_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tacógrafo Validade</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="seguro_apolice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Apólice do Seguro</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={100} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seguro_seguradora"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seguradora</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={100} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="seguro_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Validade do Seguro</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seguro_app_valor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor do Seguro APP</FormLabel>
                  <FormControl>
                    <MoneyInput value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seguro_app_vencimento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vencimento do Seguro APP</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="seguro_rc_valor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor do Seguro RC</FormLabel>
                  <FormControl>
                    <MoneyInput value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seguro_rc_vencimento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vencimento do Seguro RC</FormLabel>
                  <FormControl>
                    <Input type="date" value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tipo_bancada"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Bancada</FormLabel>
                  <Select value={field.value || ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Não informado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_TIPO_DE_BANCADA.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title="Observações" icon={MessageSquare}>
          <FormField
            control={form.control}
            name="observacoes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    maxLength={1000}
                    rows={3}
                    placeholder="Informações adicionais sobre o veículo…"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </FormSection>

        <RodapeDoFormularioEmModal formId={formId} aoCancelar={onCancelar} inline={rodapeInline} />
      </form>
    </Form>
  );
}

export default VeiculoFormulario;
